import { computed, inject, Injectable, signal } from '@angular/core';
import { ApiErrorResponse, ErrorHandlerV2Service } from '@gea/digital-ui-lib';
import { ServiceRemindersService } from '@shared/services';
import { catchError, defer, finalize, Observable, of, tap } from 'rxjs';

export interface Reminder {
  hours: number;
  date: string;
  isDeleted?: boolean;
  id: number;
  summary?: string;
}

export interface ServiceReminder {
  id: number;
  machine: string;
  totalRunningTime?: number;
  nextReminder?: number;
  componentNumberCompressor?: string;
  reminders?: Reminder[];
  lastModifiedByEmail?: string;
  lastModifiedAt?: string;
}

export interface ServiceReminderAnnunciation {
  name: string;
  isActive: boolean;
}

export interface ServiceIntervals {
  id: number;
  model: string;
  maintenanceType: string;
  suggestedRunningHoursService: number;
  bluShopKitLinkSuffix?: string;
  isRelevantToOrganization?: boolean;
}

export interface ServiceRemindersStateModel {
  serviceReminders?: ServiceReminder[];
  isLoading?: boolean;
  isSaving?: boolean;
  annunciations?: ServiceReminderAnnunciation[];
  updatedAnnunciations?: ServiceReminderAnnunciation[];
  currentReminder?: ServiceReminder;
  serviceIntervals?: ServiceIntervals[];
}

const INITIAL_STATE: ServiceRemindersStateModel = {};

/**
 * Signal store for the machine service reminders, annunciations and service intervals.
 *
 * API methods return cold observables: the request is sent when the caller subscribes.
 * Errors propagate to the caller so that components can show context-specific messages.
 * Synchronous setters update the state immediately.
 */
@Injectable({ providedIn: 'root' })
export class ServiceRemindersStore {
  private readonly serviceRemindersService = inject(ServiceRemindersService);
  private readonly errorHandler = inject(ErrorHandlerV2Service);
  private readonly state = signal<ServiceRemindersStateModel>(INITIAL_STATE);

  readonly serviceReminders = computed(() => this.state().serviceReminders);
  readonly isLoading = computed(() => this.state().isLoading);
  readonly isSaving = computed(() => this.state().isSaving);
  readonly currentReminder = computed(() => this.state().currentReminder);
  readonly annunciations = computed(() => this.state().annunciations);
  readonly updatedAnnunciations = computed(() => this.state().updatedAnnunciations);
  readonly serviceIntervals = computed(() => this.state().serviceIntervals);

  fetchServiceReminders(): Observable<ServiceReminder[]> {
    return this.withLoading(() =>
      this.serviceRemindersService
        .fetchServiceReminders()
        .pipe(tap((serviceReminders: ServiceReminder[]) => this.patch({ serviceReminders })))
    );
  }

  fetchServiceIntervals(): Observable<ServiceIntervals[]> {
    return this.withLoading(() =>
      this.serviceRemindersService.fetchServiceIntervals().pipe(
        catchError((error: ApiErrorResponse) => {
          this.errorHandler.handleError(error);
          return of([]);
        }),
        tap((serviceIntervals: ServiceIntervals[]) => this.patch({ serviceIntervals }))
      )
    );
  }

  fetchServiceReminderAnnunciations(annunciationType?: 'SHUTDOWN' | 'WARNING'): Observable<ServiceReminderAnnunciation[]> {
    return this.withLoading(() =>
      this.serviceRemindersService
        .fetchServiceReminderAnnunciations(annunciationType)
        .pipe(tap((annunciations: ServiceReminderAnnunciation[]) => this.patch({ annunciations })))
    );
  }

  saveServiceReminderAnnunciations(annunciations: ServiceReminderAnnunciation[]): Observable<ServiceReminderAnnunciation[]> {
    return this.withLoading(() =>
      this.serviceRemindersService
        .saveServiceReminderAnnunciations(annunciations)
        .pipe(tap((savedAnnunciations: ServiceReminderAnnunciation[]) => this.patch({ annunciations: savedAnnunciations })))
    );
  }

  saveServiceReminder(reminder: ServiceReminder): Observable<ServiceReminder> {
    return this.serviceRemindersService.saveServiceReminder(reminder).pipe(
      tap((savedReminder: ServiceReminder) =>
        this.patch({
          serviceReminders: this.state().serviceReminders?.map((serviceReminder) =>
            serviceReminder.machine === savedReminder.machine ? savedReminder : serviceReminder
          ),
          isLoading: false,
        })
      )
    );
  }

  setUpdatedAnnunciations(updatedAnnunciations: ServiceReminderAnnunciation[]): void {
    this.patch({ updatedAnnunciations });
  }

  setCurrentReminder(currentReminder?: ServiceReminder): void {
    this.patch({ currentReminder });
  }

  setIsSaving(isSaving: boolean): void {
    this.patch({ isSaving });
  }

  private withLoading<T>(request: () => Observable<T>): Observable<T> {
    return defer(() => {
      this.patch({ isLoading: true });
      return request().pipe(finalize(() => this.patch({ isLoading: false })));
    });
  }

  private patch(partial: Partial<ServiceRemindersStateModel>): void {
    this.state.update((current) => ({ ...current, ...partial }));
  }
}
